import { inject, Injectable } from '@angular/core';
import { DropdownData, ApiCallingV2Service, LookupsService, API_CONFIG } from '@zayan-erp/shared';
import { ProductCategoriesService, ProductCategory, ProductSubcategoriesService } from '@zayan-erp/home-building';
import { Observable, map, of, catchError } from 'rxjs';
import { OptionFilters, OptionFilterSelection } from '../models/dsm-deal-options.models';

interface ProductSubcategory {
  id?: string;
  description: string;
  code: string;
}

/** Sentinel id for "no filter"; normalizes to null when applying filters. */
// export const DSM_DEAL_OPTION_FILTER_ALL_ID = '';

// export const DSM_DEAL_OPTION_FILTER_ALL: DropdownData = {
//   id: DSM_DEAL_OPTION_FILTER_ALL_ID,
//   firstLabel: 'All'
// };

// export function withDealOptionFilterAll(items: DropdownData[]): DropdownData[] {
//   return [DSM_DEAL_OPTION_FILTER_ALL, ...items];
// }

@Injectable({ providedIn: 'root' })
export class DsmDealOptionFiltersService {
  readonly #lookupsService = inject(LookupsService);
  readonly #productCategoriesService = inject(ProductCategoriesService);
  readonly #productSubcategoriesService = inject(ProductSubcategoriesService);
  readonly #apiConfig = inject(API_CONFIG);
  readonly #http = inject(ApiCallingV2Service);
  baseUrl = this.#apiConfig.baseUrlV2;

  getRooms(showLoader = false): Observable<DropdownData[]> {
    return this.#lookupsService.readLocations(false, showLoader).pipe(map(locations => locations ?? []));
  }

  getCategories(showLoader = false): Observable<ProductCategory[]> {
    return this.#productCategoriesService.readProductCategories(showLoader).pipe(
      map(categories => categories ?? []),
      catchError(() => of([] as ProductCategory[]))
    );
  }

  getSubcategories(
    categoryId?: string | null,
    productListId?: string | null,
    showLoader = false
  ): Observable<DropdownData[]> {
    return this.#productSubcategoriesService.readSubcategories(productListId, categoryId, showLoader).pipe(
      map(
        (subcategories: ProductSubcategory[] | null) =>
          subcategories?.map(sub => this.#mapToDropdown(sub.id ?? '', sub.description, sub.code)) ?? []
      ),
      catchError(() => of([]))
    );
  }

  getOptionFilters(
    modelId: string,
    elevationId: string,
    selection: OptionFilterSelection = {}
  ): Observable<OptionFilters> {
    const params = new URLSearchParams();
    if (selection.roomId) params.set('roomId', selection.roomId);
    if (selection.categoryId) params.set('categoryId', selection.categoryId);
    if (selection.subcategoryId) params.set('subcategoryId', selection.subcategoryId);
    if (selection.catalogChannel != null) params.set('catalogChannel', String(selection.catalogChannel));
    if (selection.lotId) params.set('lotId', selection.lotId);

    const query = params.toString();
    return this.#http.getData<OptionFilters>(
      `${this.baseUrl}/modelelevationproducts/models/${modelId}/elevations/${elevationId}/filters` +
        (query ? `?${query}` : ''),
      { showLoader: false, showMessage: false, showError: false }
    );
  }

  #mapToDropdown(id: string, firstLabel: string, secondLabel?: string): DropdownData {
    return { id, firstLabel, ...(secondLabel && { secondLabel }) };
  }
  readCategoriesByModelElevation(modelId: string, elevationId: string): Observable<ProductCategory[]> {
    return this.#http.getData(
      `${this.baseUrl}/products/categories/by-model-elevation/models/${modelId}/elevations/${elevationId}?catalogChannel=1
`,
      { showLoader: true, showMessage: false }
    );
  }
}
